/**
 * @weretrade/env-helpers
 *
 * Shared environment detection and URL generation utilities
 * for all internal apps and services.
 */
export function getEnvironment() {
    if (typeof window === 'undefined')
        return 'production';
    const host = window.location.hostname;
    if (host.startsWith('staging.'))
        return 'staging';
    if (host.startsWith('dev.'))
        return 'dev';
    return 'production';
}
/**
 * Generates Cloudflare Worker URL for current environment.
 * Automatically handles services migrated to Lair404 (Docker).
 */
export function getWorkerURL(workerName, subdomain = 'weretrade.workers.dev') {
    const env = getEnvironment();
    // Lair404 Migrated Services (Production Only for now)
    if (env === 'production') {
        const lair404Mapping = {
            'neuralwhale-worker': 'https://neural-api.lair404.xyz',
            'seo-keywords-api': 'https://seo-api.lair404.xyz',
            'affiliate-revenue': 'https://affiliate-api.lair404.xyz',
            'bkb-shop-actions': 'https://bkb-actions.lair404.xyz'
        };
        if (lair404Mapping[workerName])
            return lair404Mapping[workerName];
    }
    const suffix = env !== 'production' ? `-${env}` : '';
    return `https://${workerName}${suffix}.${subdomain}`;
}
export function getPagesURL(projectName, customDomain) {
    const env = getEnvironment();
    if (env === 'production' && customDomain)
        return `https://${customDomain}`;
    const prefix = env !== 'production' ? `${env}.` : '';
    return `https://${prefix}${projectName}.pages.dev`;
}
export function getAPIBaseURL(service) {
    if (service === 'auth')
        return '/api/auth';
    const workerNames = {
        shop: 'shop-api',
        inventar: 'inventar-owl-worker',
        neural: 'neuralwhale-worker',
        'testing-metrics': 'testing-metrics',
        seo: 'seo-keywords-api',
        affiliate: 'affiliate-revenue',
        'bkb-actions': 'bkb-shop-actions'
    };
    const apiPaths = {
        shop: '/api/shop',
        inventar: '/api/inventar',
        neural: '/api/neural',
        'testing-metrics': '',
        seo: '/api',
        affiliate: '/api',
        'bkb-actions': '/api'
    };
    const baseUrl = getWorkerURL(workerNames[service]);
    return `${baseUrl}${apiPaths[service]}`;
}
export const APP_URLS = {
    get inventarOwl() { return getPagesURL('inventar-owl', 'inventar.weretrade.com'); },
    get neuralWhale() { return getPagesURL('neuralwhale', 'neural.weretrade.com'); },
    get shopBeaver() { return getPagesURL('shop-beaver', 'shopbeaver.weretrade.com'); },
    get internalDashboard() { return getPagesURL('internal-dashboard-rr5', 'webos.weretrade.com'); },
    get serviceAnt() { return getPagesURL('service-ant', 'services.weretrade.com'); },
    get testingOctopus() { return getPagesURL('testing-octopus'); },
    get metricMowl() { return getPagesURL('bauklotzbude-stats'); },
    get marketShark() { return getPagesURL('market-shark-pages', 'shark.weretrade.com'); },
    get infrastructureDog() { return getPagesURL('infrastructure-dog-pages', 'infra.weretrade.com'); },
    get designButterfly() { return getPagesURL('designbutterfly-pages'); },
    get accountFox() { return getPagesURL('accountingflare-frontend', 'fox.weretrade.com'); },
    /* authLion is the identity hub; the launcher targets the admin CONSOLE at auth.weretrade.com, not login.weretrade.com. */
    get authLion() { return getPagesURL('auth-lion', 'auth.weretrade.com') + '/console'; },
};
/* ── The app launcher's list — ONE copy ──────────────────────────────────
 *
 * Until 3.12.0 this array stood FIVE times: inventarOwl, accountingFox,
 * marketShark, infrastructureDog and shopBeaver each declared their own. They
 * agreed on the five apps and their names, and disagreed on three things:
 *
 *   - order (each put itself first),
 *   - the own-app href ("/" in its own copy, an absolute URL in the other four),
 *   - and, in shopBeaver only, the production hostnames: it linked
 *     infrastructureDog and marketShark through APP_URLS, while the other four
 *     switch to PROD_URLS on a *.weretrade.com host. So the SAME tile in the
 *     SAME launcher went to infra.weretrade.com from shopBeaver and to
 *     infrastructure.weretrade.com from everywhere else.
 *
 * marketShark's own comment named the reason to stop: a launcher that differs
 * per app is not a launcher. `launcherApps(current)` returns the list in one
 * fixed order with "/" on the current app.
 */

/**
 * Production hostnames that are NOT the APP_URLS Pages host.
 *
 * infrastructureDog: the Pages deployment fetches `/api/*` relatively and Pages
 * serves static files only, so `infra.weretrade.com` cannot run the app in
 * production — `infrastructure.weretrade.com` fronts the worker.
 * See infrastructureDog/CLAUDE.md.
 */
const PROD_URLS = {
  infrastructureDog: 'https://infrastructure.weretrade.com',
  marketShark: 'https://shark.weretrade.com',
  shopBeaver: 'https://shopbeaver.weretrade.com',
  authLion: 'https://auth.weretrade.com/console',
};

/** True on a real *.weretrade.com host — not the same question as isProduction(). */
function onProductionHost() {
  return typeof window !== 'undefined' && window.location.hostname.endsWith('.weretrade.com');
}

/** Fixed order, estate-wide. Changing it changes every launcher at once. */
const LAUNCHER_ORDER = ['inventarOwl', 'accountingFox', 'shopBeaver', 'infrastructureDog', 'marketShark', 'authLion'];

const LAUNCHER_NAMES = {
  inventarOwl: 'Inventar Owl',
  accountingFox: 'AccountingFox',
  shopBeaver: 'Shop Admin',
  infrastructureDog: 'Infrastructure Dog',
  marketShark: 'Market Shark',
  authLion: 'AuthLion',
};

function launcherHref(key) {
  if (onProductionHost() && PROD_URLS[key]) return PROD_URLS[key];
  const urls = APP_URLS;
  /* accountingFox is `accountFox` in APP_URLS — the key predates the rename. */
  return urls[key === 'accountingFox' ? 'accountFox' : key] ?? '/';
}

/**
 * The launcher tiles for the app calling it. `current` gets href "/" so the
 * launcher never leaves the app to re-enter it over the network.
 */
export function launcherApps(current) {
  return LAUNCHER_ORDER.map((key) => ({
    key,
    name: LAUNCHER_NAMES[key],
    href: key === current ? '/' : launcherHref(key),
  }));
}

export function isStaging() { return getEnvironment() === 'staging'; }
export function isProduction() { return getEnvironment() === 'production'; }
export function isDev() { return getEnvironment() === 'dev'; }
export function getEnvironmentDisplayName() {
    const env = getEnvironment();
    return env.charAt(0).toUpperCase() + env.slice(1);
}
export function getLoginURL(redirectUrl) {
    const redirect = redirectUrl || (typeof window !== 'undefined' ? window.location.href : '');
    const encodedRedirect = encodeURIComponent(redirect);
    const env = getEnvironment();
    if (env === 'staging')
        return `https://staging.auth-lion.pages.dev/login?redirect=${encodedRedirect}`;
    if (env === 'dev')
        return `https://dev.auth-lion.pages.dev/login?redirect=${encodedRedirect}`;
    return `https://login.weretrade.com/login?redirect=${encodedRedirect}`;
}
